import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, User } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import SearchInput from '../components/SearchInput';
import { employeesAPI, hrmsAPI } from '../services/api';
import { filterBySearch } from '../utils/search';

function formatCurrency(amount) {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);
}

const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

export default function Employees() {
  const [employees, setEmployees] = useState([]);
  const [lookups, setLookups] = useState({ departments: [], branches: [], policies: [] });
  const [loading, setLoading] = useState(true);
  const [hrmsSyncEnabled, setHrmsSyncEnabled] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    employee_code: '', full_name: '', designation: '', date_of_joining: '', date_of_birth: '',
    gender: 'male', mobile_number: '', email: '', salary_ctc: '',
    blood_group: 'O+', employment_type: 'permanent', hrms_integration_id: '',
    aadhaar: '', pan: '', department_id: '', branch_id: '', policy_id: '',
    create_portal_login: true, portal_email: '', portal_password: 'Admin@123',
  });
  const [enrollmentSuccess, setEnrollmentSuccess] = useState(null);
  const [premiumPreview, setPremiumPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [search, setSearch] = useState('');

  const filteredEmployees = useMemo(
    () => filterBySearch(employees, search, [
      'full_name',
      'employee_code',
      'email',
      'department_name',
      'policy_number',
      'enrollment_status',
    ]),
    [employees, search]
  );

  const loadEmployees = () => {
    employeesAPI.list()
      .then(({ data }) => setEmployees(data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadEmployees();
    employeesAPI.lookups().then(({ data }) => setLookups(data));
    hrmsAPI.config()
      .then(({ data }) => setHrmsSyncEnabled(Boolean(data.syncEnabled)))
      .catch(() => setHrmsSyncEnabled(false));
  }, []);

  useEffect(() => {
    if (!showForm || !form.policy_id || !form.date_of_birth) {
      setPremiumPreview(null);
      setPreviewError(null);
      return undefined;
    }

    const timer = setTimeout(async () => {
      setPreviewLoading(true);
      setPreviewError(null);
      try {
        const { data } = await employeesAPI.calculatePremium({
          policy_id: parseInt(form.policy_id, 10),
          date_of_birth: form.date_of_birth,
          gender: form.gender,
          date_of_joining: form.date_of_joining,
          enrollment_date: form.date_of_joining,
          employment_type: form.employment_type,
        });
        setPremiumPreview(data.premium);
      } catch (err) {
        setPremiumPreview(null);
        setPreviewError(err.response?.data?.error || 'Unable to calculate premium');
      } finally {
        setPreviewLoading(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [showForm, form.policy_id, form.date_of_birth, form.date_of_joining, form.gender, form.employment_type]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await employeesAPI.create({
        ...form,
        department_id: parseInt(form.department_id, 10),
        branch_id: parseInt(form.branch_id, 10),
        policy_id: parseInt(form.policy_id, 10),
        salary_ctc: parseFloat(form.salary_ctc),
        portal_email: form.portal_email || form.email,
      });
      setShowForm(false);
      setEnrollmentSuccess(data.portalLogin?.email
        ? { email: data.portalLogin.email, password: data.portalLogin.temporaryPassword, warning: null }
        : data.portalLogin?.error
          ? { email: null, password: null, warning: data.portalLogin.error }
          : null);
      setForm({
        employee_code: '', full_name: '', designation: '', date_of_joining: '', date_of_birth: '',
        gender: 'male', mobile_number: '', email: '', salary_ctc: '',
        blood_group: 'O+', employment_type: 'permanent', hrms_integration_id: '',
        aadhaar: '', pan: '', department_id: '', branch_id: '', policy_id: '',
        create_portal_login: true, portal_email: '', portal_password: 'Admin@123',
      });
      loadEmployees();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to enroll employee');
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Employee Enrollment</h1>
          <p className="text-slate-500 mt-1">Enroll employees with auto premium calculation (M3, M5, M6)</p>
        </div>
        <button onClick={() => setShowForm(!showForm)} className="btn-primary flex items-center gap-2 self-start">
          <Plus className="w-4 h-4" />
          Enroll Employee
        </button>
      </div>

      {hrmsSyncEnabled && (
        <div className="p-4 bg-sky-50 border border-sky-200 rounded-lg text-sm text-sky-900">
          HRMS sync is enabled. Prefer synced employees (HRMS badge). Master identity fields are locked on edit — assign policy/enrollment only. Super Admin can run sync from <Link to="/hrms-sync" className="underline font-medium">HRMS Sync</Link>.
        </div>
      )}
      {enrollmentSuccess?.email && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
          <strong>Employee enrolled.</strong> Portal login: <strong>{enrollmentSuccess.email}</strong> / <strong>{enrollmentSuccess.password}</strong>
          <span className="block text-green-700 mt-1">Share these credentials with the employee. You can also manage logins under Portal Logins.</span>
        </div>
      )}
      {enrollmentSuccess?.warning && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          Employee enrolled, but portal login was not created: {enrollmentSuccess.warning}. Create it from <strong>Portal Logins</strong>.
        </div>
      )}

      {showForm && (
        <form onSubmit={handleSubmit} className="card space-y-4">
          <h3 className="font-semibold text-lg">New Enrollment</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="label">Full Name *</label>
              <input className="input-field" required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
            </div>
            <div>
              <label className="label">Designation *</label>
              <input className="input-field" required value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} />
            </div>
            <div>
              <label className="label">Email *</label>
              <input
                type="email"
                className="input-field"
                required
                value={form.email}
                onChange={(e) => setForm({
                  ...form,
                  email: e.target.value,
                  portal_email: form.portal_email || e.target.value,
                })}
              />
            </div>
            <div>
              <label className="label">Mobile *</label>
              <input className="input-field" required value={form.mobile_number} onChange={(e) => setForm({ ...form, mobile_number: e.target.value })} />
            </div>
            <div>
              <label className="label">Date of Birth *</label>
              <input type="date" className="input-field" required value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} />
            </div>
            <div>
              <label className="label">Date of Joining *</label>
              <input type="date" className="input-field" required value={form.date_of_joining} onChange={(e) => setForm({ ...form, date_of_joining: e.target.value })} />
            </div>
            <div>
              <label className="label">Gender *</label>
              <select className="input-field" value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })}>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="label">Blood Group *</label>
              <select className="input-field" value={form.blood_group} onChange={(e) => setForm({ ...form, blood_group: e.target.value })}>
                {BLOOD_GROUPS.map((bg) => <option key={bg} value={bg}>{bg}</option>)}
              </select>
            </div>
            <div>
              <label className="label">CTC (INR) *</label>
              <input type="number" className="input-field" required value={form.salary_ctc} onChange={(e) => setForm({ ...form, salary_ctc: e.target.value })} />
            </div>
            <div>
              <label className="label">Department *</label>
              <select className="input-field" required value={form.department_id} onChange={(e) => setForm({ ...form, department_id: e.target.value })}>
                <option value="">Select</option>
                {lookups.departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Branch *</label>
              <select className="input-field" required value={form.branch_id} onChange={(e) => setForm({ ...form, branch_id: e.target.value })}>
                <option value="">Select</option>
                {lookups.branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Policy *</label>
              <select className="input-field" required value={form.policy_id} onChange={(e) => setForm({ ...form, policy_id: e.target.value })}>
                <option value="">Select</option>
                {lookups.policies.map((p) => <option key={p.id} value={p.id}>{p.company_name} - {p.policy_number}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Employment Type *</label>
              <select className="input-field" value={form.employment_type} onChange={(e) => setForm({ ...form, employment_type: e.target.value })}>
                <option value="permanent">Permanent</option>
                <option value="contract">Contract</option>
                <option value="probation">Probation</option>
              </select>
            </div>
            <div>
              <label className="label">Employee ID / HRMS</label>
              <input className="input-field" value={form.employee_code} placeholder="Auto-generated if empty" onChange={(e) => setForm({ ...form, employee_code: e.target.value })} />
            </div>
            <div>
              <label className="label">HRMS Integration ID</label>
              <input className="input-field" value={form.hrms_integration_id} onChange={(e) => setForm({ ...form, hrms_integration_id: e.target.value })} />
            </div>
            <div>
              <label className="label">Aadhaar</label>
              <input className="input-field" maxLength={12} value={form.aadhaar} onChange={(e) => setForm({ ...form, aadhaar: e.target.value })} />
            </div>
            <div>
              <label className="label">PAN</label>
              <input className="input-field" maxLength={10} value={form.pan} onChange={(e) => setForm({ ...form, pan: e.target.value })} />
            </div>
          </div>

          <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg space-y-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.create_portal_login}
                onChange={(e) => setForm({ ...form, create_portal_login: e.target.checked })}
              />
              <span className="font-medium text-slate-800">Create employee portal login</span>
            </label>
            {form.create_portal_login && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">Portal Login Email</label>
                  <input
                    type="email"
                    className="input-field"
                    placeholder="Defaults to work email"
                    value={form.portal_email}
                    onChange={(e) => setForm({ ...form, portal_email: e.target.value })}
                  />
                </div>
                <div>
                  <label className="label">Temporary Password</label>
                  <input
                    type="text"
                    className="input-field"
                    minLength={8}
                    value={form.portal_password}
                    onChange={(e) => setForm({ ...form, portal_password: e.target.value })}
                  />
                </div>
              </div>
            )}
          </div>

          {(premiumPreview || previewLoading || previewError) && (
            <div className="p-4 bg-primary-50 border border-primary-100 rounded-lg text-sm">
              <h4 className="font-semibold text-primary-900 mb-2">Premium Preview (Age Slab)</h4>
              {previewLoading && <p className="text-slate-500">Calculating…</p>}
              {previewError && <p className="text-red-600">{previewError}</p>}
              {premiumPreview && !previewLoading && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <span className="text-slate-500 block">Age at enrollment</span>
                    <span className="font-medium">{premiumPreview.age} yrs</span>
                  </div>
                  <div>
                    <span className="text-slate-500 block">Age slab</span>
                    <span className="font-medium">{premiumPreview.slabInfo?.ageBand || '—'}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 block">Annual premium</span>
                    <span className="font-medium">{formatCurrency(premiumPreview.annualPremium)}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 block">Pro-rata ({premiumPreview.remainingDays} days)</span>
                    <span className="font-medium">{formatCurrency(premiumPreview.proRataPremium)}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 block">GST (18%)</span>
                    <span className="font-medium">{formatCurrency(premiumPreview.gstAmount)}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 block">Total payable</span>
                    <span className="font-semibold text-primary-700">{formatCurrency(premiumPreview.totalPremium)}</span>
                  </div>
                  {premiumPreview.slabInfo?.riskCategory && (
                    <div>
                      <span className="text-slate-500 block">Risk category</span>
                      <span className="font-medium capitalize">{premiumPreview.slabInfo.riskCategory}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="flex gap-3">
            <button type="submit" className="btn-primary">Enroll & Calculate Premium</button>
            <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
          </div>
        </form>
      )}

      <SearchInput
        className="max-w-md"
        placeholder="Search by name, code, department, policy…"
        value={search}
        onChange={setSearch}
      />

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left text-slate-500">
              <th className="pb-3 pr-4 font-medium">Employee</th>
              <th className="pb-3 pr-4 font-medium hidden sm:table-cell">Department</th>
              <th className="pb-3 pr-4 font-medium hidden md:table-cell">Policy</th>
              <th className="pb-3 pr-4 font-medium">Premium</th>
              <th className="pb-3 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredEmployees.map((emp) => (
              <tr key={emp.id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="py-3 pr-4">
                  <div className="flex items-center gap-2">
                    <div className="bg-primary-50 p-1.5 rounded-full">
                      <User className="w-4 h-4 text-primary-600" />
                    </div>
                    <div>
                      <Link to={`/employees/${emp.id}`} className="font-medium text-primary-600 hover:underline">{emp.full_name}</Link>
                      <div className="text-xs text-slate-400 flex flex-wrap items-center gap-2">
                        <span>{emp.employee_code} · Age {emp.age ?? '—'}</span>
                        {emp.hrms_integration_id && (
                          <span className="px-1.5 py-0.5 bg-sky-50 text-sky-700 rounded text-[10px] font-medium uppercase tracking-wide">
                            HRMS{emp.last_synced_at ? ' synced' : ''}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="py-3 pr-4 hidden sm:table-cell text-slate-600">{emp.department_name}</td>
                <td className="py-3 pr-4 hidden md:table-cell text-slate-600">{emp.policy_number}</td>
                <td className="py-3 pr-4 font-medium text-slate-900">{formatCurrency(emp.total_premium)}</td>
                <td className="py-3">
                  <span className="px-2 py-1 bg-green-50 text-green-700 text-xs rounded-full capitalize">
                    {emp.enrollment_status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {employees.length === 0 && (
          <p className="text-center py-8 text-slate-500">No enrolled employees yet.</p>
        )}
        {employees.length > 0 && filteredEmployees.length === 0 && (
          <p className="text-center py-8 text-slate-500">No employees match your search.</p>
        )}
      </div>
    </div>
  );
}
